import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { OfferFormData, OfferItem, ProductData, OfferDraftItem, OfferDraftOption } from '../types/offerTypes';
import { Seller, Buyer, InvoiceItem, CalculatedTotals } from '../types/invoiceTypes';
import { generateOffer, listOffers, getOfferDetails, saveDraft, generateOfferPreview, listOfferDrafts, getDraftDetails, deleteDraft } from '../api/offerApi';
import { getUserData, saveSellerData, refreshUserData } from '../firebase/firestoreUtils';
import { generateOfferNumber } from '../utils/numberGenerators';
import { calculateTotals } from '../utils/calculationUtils';
import BuyerDetailsForm from '../components/invoice/BuyerDetailsForm';
import InvoiceItemsForm from '../components/invoice/InvoiceItemsForm';
import OfferFormActions from '../components/offer/OfferFormActions';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ErrorDisplay from '../components/ui/ErrorDisplay';
import { getAuth } from 'firebase/auth';
import logger from '../utils/logger';
import Select, { SingleValue } from 'react-select';
import { reactSelectTokenStyles } from '../styles/reactSelectTokenStyles';
import OfferForm from '../components/offer/OfferForm';
import OfferDraftSelector from '../components/offer/OfferDraftSelector';
import { useCustomerManagement } from '../hooks/invoice/useCustomerManagement';
import { useProductManagement } from '../hooks/invoice/useProductManagement';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import DocumentLanguageSelector from '../components/invoice/DocumentLanguageSelector';
import TranslationReviewModal from '../components/translation/TranslationReviewModal';
import useTranslationReview from '../hooks/useTranslationReview';
import { formatDate as formatDateCentral } from '../utils/formatters';

// Wiederherstellen der Typdefinitionen
interface OfferListItem {
    id: string;
    offerNumber: string;
    offerDate: string;
    buyerName: string;
    status: string;
    totalAmount: number;
    currency: string;
}

interface OfferSelectOption {
    value: string; // Offer ID
    label: string; // Angezeigter Text
}


// Default-Struktur für ein neues Angebot (global definiert)
const defaultOfferFormData: OfferFormData = {
    offerDate: new Date().toISOString().split('T')[0],
    validUntil: '', 
    currency: 'EUR',
    seller: {} as Seller, 
    buyer: { name: '', street: '', zip: '', city: '', country: 'DE' }, 
    items: [{ id: crypto.randomUUID(), name: '', description: '', quantity: 1, netPrice: 0, unitPrice: 0, taxRate: 19 }],
    notes: '',
    offerText: '',
    // NEU: Mehrsprachigkeit - Standard ist Deutsch
    documentLanguage: 'de',
};

// Default Seller Data (neu global definiert)
const defaultSellerData: Seller = {
    name: '',
    id: '',
    street: '',
    zip: '',
    city: '',
    country: 'DE',
    vatNumber: null,
    taxNumber: null,
    street2: null,
    globalId: null,
    globalIdScheme: null,
    // NEU: Handelsregister-Felder
    commercialRegisterNumber: null,
    commercialRegisterLocation: null,
    contact: { name: null, department: null, phone: null, fax: null, email: null, website: null },
    communication: [],
    bankDetails: { iban: '', bic: '', bankName: '' },
    logoUrl: null,
    invoiceNumberFormat: null,
    invoiceNumberCurrent: null,
    invoiceNumberStart: null,
    offerNumberFormat: null,
    offerNumberCurrent: null,
    offerNumberStart: null,
};

const OfferDashboard: React.FC = () => {
    const { t, i18n } = useTranslation('offer');
    const auth = useAuth(); // Korrekte Verwendung des useAuth Hooks als Objekt
    const { currentUser, loading } = auth;
    
    // Firebase Auth-Token holen (wird für API-Anfragen benötigt)
    const [token, setToken] = useState<string | null>(null);

    useEffect(() => {
        // Token aus Firebase Auth holen, wenn der Benutzer angemeldet ist
        const getToken = async () => {
            if (currentUser) {
                try {
                    const authToken = await getAuth().currentUser?.getIdToken(true);
                    setToken(authToken || null);
                } catch (error) {
                    console.error("Fehler beim Laden des Auth-Tokens:", error);
                    setToken(null);
                }
            } else {
                setToken(null);
            }
        };
        getToken();
    }, [currentUser]);

    const [offerData, setOfferData] = useState<OfferFormData>(defaultOfferFormData);
    
    // NEU: Email-Versand an Kunden
    const [sendToEmail, setSendToEmail] = useState<string | null>(null);

    // State-Variablen
    const [offerNumberPreview, setOfferNumberPreview] = useState<string>(''); // Umbenannt von offerNumber
    const [sellerData, setSellerData] = useState<Seller | null>(null);
    const [sellerDataLoading, setSellerDataLoading] = useState<boolean>(true);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null); // Für die Vorschau - VORERST BEHALTEN, falls doch wieder benötigt
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true); // Initiales Laden der Verkäuferdaten
    const [isGenerating, setIsGenerating] = useState<boolean>(false); // Generierung läuft
    const [calculatedTotals, setCalculatedTotals] = useState<CalculatedTotals>({
        totalNet: 0,
        totalTax: 0,
        grandTotal: 0,
        taxSummary: {}
    });

    // Wiederherstellen der States für Angebotsliste und Auswahl
    const [availableOffers, setAvailableOffers] = useState<OfferListItem[]>([]);
    const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
    const [isLoadingOffers, setIsLoadingOffers] = useState<boolean>(false);
    const [offerListError, setOfferListError] = useState<string | null>(null);
    const [isLoadingOfferDetails, setIsLoadingOfferDetails] = useState<boolean>(false);

    // Draft-Management States
    const [availableDrafts, setAvailableDrafts] = useState<OfferDraftItem[]>([]);
    const [selectedDraftId, setSelectedDraftId] = useState<string | null>(null);
    const [isLoadingDrafts, setIsLoadingDrafts] = useState<boolean>(false);
    const [draftListError, setDraftListError] = useState<string | null>(null);
    const [isLoadingDraftDetails, setIsLoadingDraftDetails] = useState<boolean>(false);
    const [isSavingDraft, setIsSavingDraft] = useState<boolean>(false);
    const [isGeneratingPreview, setIsGeneratingPreview] = useState<boolean>(false);

    // NEU: Customer Management Hook (genau wie in InvoiceDashboard)
    const {
        customerMeta, 
        checkForUpdate: checkCustomerUpdate, 
        handleCustomerSelection,
        dialogOpen: customerDialogOpen,
        setDialogOpen: setCustomerDialogOpen,
        dialogConfig: customerDialogConfig,
        handleDialogAction: handleCustomerDialogAction,
        releaseCustomerMetaLock,
        getCurrentCustomerMeta
    } = useCustomerManagement();

    // NEU: Product Management Hook (genau wie in InvoiceDashboard)
    const {
        productMetaForItems,
        checkAndHandleProductUpdate,
        handleProductSelection: handleProductSelectionForHook,
        resetProductManagement,
        dialogOpen: productDialogOpen,
        setDialogOpen: setProductDialogOpen,
        dialogConfig: productDialogConfig,
        handleDialogAction: handleProductDialogAction,
        getProductMetaForItems
    } = useProductManagement();

    const {
        isModalOpen: isTranslationModalOpen,
        isLoading: isTranslationLoading,
        fields: translationFields,
        handleFieldChange: handleTranslationFieldChange,
        prepareAndOpenModal: prepareTranslationModal,
        handleConfirm: confirmTranslation,
        handleCancel: cancelTranslation,
    } = useTranslationReview();

    // Extrahiere fetchOffers in ein useCallback
    const fetchOffers = useCallback(async () => {
        if (token) {
            setIsLoadingOffers(true);
            setOfferListError(null);
            try {
                const offers = await listOffers(token);
                setAvailableOffers(offers);
                logger.info("Angebotsliste geladen:", offers);
            } catch (err: any) {
                logger.error(err, "Fehler beim Laden der Angebotsliste");
                setOfferListError(err.message || 'Fehler beim Laden der Angebotsliste.');
            }
             setIsLoadingOffers(false);
        }
    }, [token]); // Abhängigkeit: token

    // Draft-Fetch-Funktion
    const fetchDrafts = useCallback(async () => {
        if (token) {
            setIsLoadingDrafts(true);
            setDraftListError(null);
            try {
                const drafts = await listOfferDrafts(token);
                setAvailableDrafts(drafts);
                logger.info("Draft-Liste geladen:", drafts);
            } catch (err: any) {
                logger.error(err, "Fehler beim Laden der Draft-Liste");
                setDraftListError(err.message || 'Fehler beim Laden der Draft-Liste.');
            }
            setIsLoadingDrafts(false);
        }
    }, [token]);

    // Initiales Laden der Angebotsliste und Drafts
    useEffect(() => {
        fetchOffers();
        fetchDrafts();
    }, [fetchOffers, fetchDrafts]);

    // Verkäuferdaten laden (inkl. Angebotsnummer-Vorschau-Logik)
    const loadUserData = useCallback(async () => {
        if (currentUser?.uid) {
            try {
                setSellerDataLoading(true);
                setError(null);
                
                // Auth-Context verwenden statt direkter Firestore-Zugriffe
                if (auth.sellerDataLoading) {
                    await auth.refreshUserData();
                }
                
                const seller = auth.sellerData;
                setSellerData(seller);
                
                // Setze Verkäuferdaten im Angebot und Standardwerte
                setOfferData((prev: OfferFormData) => ({
                    ...prev,
                    seller: seller || prev.seller,
                    currency: seller?.defaultCurrency || prev.currency || 'EUR',
                    notes: seller?.defaultNotes || prev.notes || '',
                    offerText: prev.offerText || '', // Behalte OfferText bei
                    // Setze Items mit Standard-Steuersatz und Einheitencode falls vorhanden
                    items: prev.items.map((item: OfferItem) => ({
                        ...item,
                        taxRate: item.taxRate === 19 ? (seller?.defaultTaxRate ?? 19) : item.taxRate,
                        unitCode: item.unitCode || seller?.defaultUnitCode || 'H87',
                    })),
                }));
                
                // Generiere die nächste Angebotsnummer für die Vorschau
                // Verwende Standard-Format wenn keines konfiguriert ist
                const offerFormat = seller?.offerNumberFormat || 'ANGEBOT-{number:4}';
                const currentRaw = seller?.offerNumberCurrent;
                const current = currentRaw !== undefined && currentRaw !== null ? parseInt(String(currentRaw), 10) : null;
                const start = parseInt(String(seller?.offerNumberStart ?? 1), 10);
                
                let numberForPreview: number;

                if (current === null || current < start) {
                    numberForPreview = start;
                } else {
                    numberForPreview = current + 1;
                }
                
                const nextOfferNumberFormatted = generateOfferNumber(
                    offerFormat,
                    numberForPreview, 
                    new Date()
                );
                setOfferNumberPreview(nextOfferNumberFormatted); // State für Vorschau aktualisieren
            } catch (err: any) {
                setError('Fehler beim Laden der Benutzerdaten: ' + err.message);
                console.error("Error loading user data:", err);
            } finally {
                setSellerDataLoading(false);
                setIsLoading(false);
            }
        } else {
            setSellerData(null);
            setSellerDataLoading(false);
            setIsLoading(false);
        }
    }, [currentUser?.uid, auth]);

    useEffect(() => {
        if (!loading) {
            loadUserData();
        }
    }, [currentUser, loading, loadUserData]);

    // Aktualisiere Berechnete Summen, wenn sich items oder currency ändert
    useEffect(() => {
        const totals = calculateTotals(offerData.items);
        setCalculatedTotals(totals);
    }, [offerData.items]);

    // Änderungen an Angebotsdaten verarbeiten
    const handleBuyerChange = useCallback((updatedBuyer: Buyer) => {
        setOfferData((prev: OfferFormData) => ({ ...prev, buyer: updatedBuyer })); // Typ für prev
        
        // checkCustomerUpdate wird nur beim Klick auf "Angebot generieren" aufgerufen
        // um zu verhindern, dass der Dialog sofort beim Ausfüllen der Felder erscheint
    }, []);

    // Handler for InvoiceItemsForm component
    const handleItemsFormChange = useCallback((updatedItems: InvoiceItem[] | ((prevItems: InvoiceItem[]) => InvoiceItem[])) => {
        if (typeof updatedItems === 'function') {
            setOfferData((prev: OfferFormData) => {
                const currentInvoiceItems = prev.items as unknown as InvoiceItem[]; // Type assertion
                const newInvoiceItems = updatedItems(currentInvoiceItems);
                const newOfferItems = newInvoiceItems.map(item => ({
                    ...item,
                    unitPrice: item.unitPrice || item.netPrice // Ensure unitPrice exists
                })) as OfferItem[];
                return { ...prev, items: newOfferItems };
            });
        } else {
            const newOfferItems = updatedItems.map(item => ({
                ...item,
                unitPrice: item.unitPrice || item.netPrice // Ensure unitPrice exists
            })) as OfferItem[];
            setOfferData((prev: OfferFormData) => ({ ...prev, items: newOfferItems }));
        }
    }, []);
    
    // Funktion zum Zurücksetzen des Formulars (vereinfacht, keine Angebotsauswahl mehr)
    const resetOfferForm = useCallback(() => {
        setOfferData(prev => ({
            ...defaultOfferFormData, // Nutze globalen Default
            seller: sellerData || defaultSellerData, // Verwende globalen Default-Seller
            currency: sellerData?.defaultCurrency || defaultOfferFormData.currency,
            notes: sellerData?.defaultNotes || defaultOfferFormData.notes,
            offerText: '', // Reset offerText
            items: [{ 
                id: crypto.randomUUID(), 
                name: '', 
                description: '', 
                quantity: 1, 
                netPrice: 0, 
                unitPrice: 0, // Wichtig: Muss initialisiert werden, da Pflichtfeld
                taxRate: sellerData?.defaultTaxRate ?? 19,
                unitCode: sellerData?.defaultUnitCode || 'H87',
            }],
            offerNumberFormat: sellerData?.offerNumberFormat,
            offerNumberCurrent: sellerData?.offerNumberCurrent,
        }));
        // Wichtig: Auch selectedOfferId zurücksetzen
        setSelectedOfferId(null);
        setPdfUrl(null); 
        setError(null);
        // NEU: Auch Produktverwaltung zurücksetzen (genau wie in InvoiceDashboard)
        resetProductManagement();
        setSendToEmail(null);
        loadUserData(); // Reload user data to update preview
    }, [sellerData, loadUserData, resetProductManagement]);

    // Erhöhe die Angebotsnummer nach erfolgreicher Erstellung (Logik zur Vorschau-Aktualisierung)
    const updateOfferNumberPreviewAfterIncrement = async () => {
        // Verwende Standard-Format wenn keines konfiguriert ist
        const offerFormat = auth.sellerData?.offerNumberFormat || 'ANGEBOT-{number:4}';
        const currentRaw = auth.sellerData?.offerNumberCurrent;
        const current = currentRaw !== undefined && currentRaw !== null ? parseInt(String(currentRaw), 10) : null;
        const start = parseInt(String(auth.sellerData?.offerNumberStart ?? 1), 10);
        
        let numberForPreview: number;

        if (current === null || current < start) {
             numberForPreview = start;
        } else {
             numberForPreview = current + 1;
        }
                            
        const newOfferNumberFormatted = generateOfferNumber(
            offerFormat,
            numberForPreview, 
            new Date(offerData.offerDate) 
        );
        setOfferNumberPreview(newOfferNumberFormatted); // Aktualisiere die Anzeige
    };

    // Wiederherstellen: Handler zum Auswählen eines bestehenden Angebots
    const handleSelectOffer = async (selectedOption: SingleValue<OfferSelectOption>) => {
        const offerId = selectedOption ? selectedOption.value : null;
        
        if (offerId === null) {
            // Benutzer wählt "Neues Angebot" -> Formular zurücksetzen
            resetOfferForm();
            return;
        }

        if (!token) {
            setError("Authentifizierungstoken fehlt. Bitte neu anmelden.");
            return;
        }

        setSelectedOfferId(offerId);
        setIsLoadingOfferDetails(true);
        setError(null);
        setPdfUrl(null);

        try {
            const details = await getOfferDetails(offerId, token);
            logger.info("Geladene Angebotsdetails:", details);
            
            // Setze Formulardaten basierend auf den geladenen Details
            setOfferData((prevOfferData) => ({
                ...prevOfferData, // Behalte bestehende Daten, die nicht überschrieben werden
                ...details, // Übernehme Top-Level Felder wie id, status, etc. vom geladenen Angebot
                
                // Greife auf die verschachtelten Daten aus rawData zu
                items: Array.isArray(details.rawData?.items) ? details.rawData.items : [],
                buyer: details.rawData?.buyer || defaultOfferFormData.buyer,
                
                // Felder, die vom geladenen Angebot aus rawData übernommen werden sollen
                currency: details.rawData?.currency || prevOfferData.currency || defaultOfferFormData.currency,
                notes: details.rawData?.notes || prevOfferData.notes || defaultOfferFormData.notes,
                offerText: details.rawData?.offerText || prevOfferData.offerText || defaultOfferFormData.offerText,
                validUntil: details.rawData?.validUntil || prevOfferData.validUntil || '',
                // ⭐ NEU: Dokumentsprache vom Original-Angebot übernehmen
                documentLanguage: details.rawData?.documentLanguage || 'de',

                // Überschreibe spezifische Felder für das *neue* Angebot, das auf dem alten basiert
                offerNumber: undefined, // Wird bei Bedarf neu generiert oder bleibt leer
                offerDate: new Date().toISOString().split('T')[0], // Setze auf aktuelles Datum
                
                // Verkäuferdaten und Angebotsnummernformat/aktueller Zähler aus dem aktuellen Verkäuferprofil übernehmen
                seller: sellerData || defaultOfferFormData.seller, // Aktuelle Verkäuferdaten
                offerNumberFormat: sellerData?.offerNumberFormat, 
                offerNumberCurrent: sellerData?.offerNumberCurrent,
                
                basedOnOfferId: offerId, // Setze die ID des Basis-Angebots
            }));
            // Kein loadUserData() hier, da wir die Daten gerade gesetzt haben
        } catch (err: any) {
            logger.error(err, `Fehler beim Laden der Details für Angebot ${offerId}`);
            setError(err.message || 'Fehler beim Laden der Angebotsdetails.');
            setSelectedOfferId(null); // Auswahl zurücksetzen bei Fehler
        } finally {
            setIsLoadingOfferDetails(false);
        }
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
         setOfferData((prev: OfferFormData) => ({ ...prev, offerDate: e.target.value })); // Typ für prev
    };

    const handleValidUntilChange = (e: React.ChangeEvent<HTMLInputElement>) => {
         setOfferData((prev: OfferFormData) => ({ ...prev, validUntil: e.target.value })); // Typ für prev
    };

    const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
         setOfferData((prev: OfferFormData) => ({ ...prev, notes: e.target.value })); // Typ für prev
    };
    
    const handleOfferTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
         setOfferData((prev: OfferFormData) => ({ ...prev, offerText: e.target.value })); // Typ für prev
    };

    // Aktualisiere Berechnete Summen
    const updateCalculatedTotals = useCallback(() => {
        try {
            const itemsForCalc = offerData.items as unknown as InvoiceItem[];
            const totals = calculateTotals(itemsForCalc);
            setCalculatedTotals(totals); // Store calculated totals
        } catch (error) {
            console.error("Error calculating totals:", error);
            setError("Fehler bei der Berechnung der Summen.");
        }
    }, [offerData.items]);

    useEffect(() => {
        updateCalculatedTotals();
    }, [offerData.items, updateCalculatedTotals]);

    // Hilfsfunktion: Angebot tatsächlich generieren und senden (ohne Validierung)
    const submitOfferToBackend = useCallback(async (translationOverrides?: {
        items?: OfferFormData['items'];
        notes?: string;
        offerText?: string;
    }) => {
        if (!token || !sellerData) return;

        setIsGenerating(true);
        setError(null);
        setPdfUrl(null);

        // Aktuelle Metadaten holen
        const currentProductMeta = getProductMetaForItems();
        const currentCustomerMeta = getCurrentCustomerMeta();

        const resolvedItems = (translationOverrides?.items ?? offerData.items).map((item: OfferFormData['items'][number]) => ({
            ...item,
            itemAction: currentProductMeta[item.id]?.action || 'none',
            productId: currentProductMeta[item.id]?.productId || null,
        }));

        const dataToSend: OfferFormData = {
            ...offerData,
            seller: sellerData!,
            calculatedTotals: calculatedTotals,
            customerAction: currentCustomerMeta.action,
            customerDocumentId: currentCustomerMeta.customerId,
            notes: translationOverrides?.notes ?? offerData.notes,
            offerText: translationOverrides?.offerText ?? offerData.offerText,
            items: resolvedItems,
            sendToEmail: sendToEmail || null,
        };
        
        try {
            const { pdfBlob, offerId: createdOfferId, filename } = await generateOffer(dataToSend, token);
            
            if (!pdfBlob) {
                throw new Error("Kein PDF-Blob vom Backend erhalten.");
            }

            const correctedPdfBlob = pdfBlob.type !== 'application/pdf' 
                ? new Blob([pdfBlob], { type: 'application/pdf' })
                : pdfBlob;
                
            const url = URL.createObjectURL(correctedPdfBlob);
            
            // Verwende den Dateinamen vom Backend (professionell formatiert), 
            // oder generiere einen Fallback
            let downloadFilename = filename;
            if (!downloadFilename) {
                const customerName = (offerData.buyer.companyName || offerData.buyer.personName || offerData.buyer.name || 'Kunde')
                    .replace(/[/\\?%*:|"<>äöüÄÖÜß]/g, (char) => {
                        const umlautMap: Record<string, string> = { 'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'Ä': 'Ae', 'Ö': 'Oe', 'Ü': 'Ue', 'ß': 'ss' };
                        return umlautMap[char] || '_';
                    })
                    .replace(/\s+/g, '_')
                    .substring(0, 50);
                const safeOfferNumber = offerNumberPreview?.replace(/[/\\?%*:|"<>]/g, '_') || 'Angebot'; 
                downloadFilename = `${customerName}-Angebot-${safeOfferNumber}.pdf`;
            }
            
            const link = document.createElement('a');
            link.href = url;
            link.download = downloadFilename;
            link.setAttribute('type', 'application/pdf');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            setTimeout(() => URL.revokeObjectURL(url), 5000);
            
            await auth.refreshUserData(); 
            await updateOfferNumberPreviewAfterIncrement();

            if (createdOfferId) {
                logger.info(`Angebot erfolgreich erstellt mit ID: ${createdOfferId}.`);
            } else {
                logger.warn("Angebot erstellt, aber keine ID vom Backend erhalten.");
            }
            
            resetOfferForm(); 
            await fetchOffers();
            releaseCustomerMetaLock();
            
            if (selectedDraftId) {
                try {
                    await deleteDraft(selectedDraftId, token);
                    await fetchDrafts();
                    setSelectedDraftId(null);
                } catch (draftError) {
                    logger.warn("Draft konnte nicht gelöscht werden:", draftError);
                }
            }
            
            setError(null);
        } catch (err: any) {
            console.error('Fehler beim Generieren des Angebots:', err);
            setError(`Fehler: ${err.message || 'Unbekannter Fehler beim Generieren des Angebots'}`);
        } finally {
            setIsGenerating(false);
        }
    }, [token, sellerData, offerData, calculatedTotals, getProductMetaForItems, getCurrentCustomerMeta, 
        offerNumberPreview, auth, resetOfferForm, fetchOffers, releaseCustomerMetaLock, 
        selectedDraftId, fetchDrafts, updateOfferNumberPreviewAfterIncrement]);

    // Angebot generieren (mit Validierung)
    const handleGenerateOffer = async () => {
        if (!token) {
            setError("Authentifizierungstoken fehlt. Bitte neu anmelden.");
            return;
        }
        if (!sellerData) {
            setError("Verkäuferdaten fehlen.");
            return;
        }

        // Erst Kundenvalidierung durchführen - falls Dialog erscheint, wird hier gestoppt
        const customerValidationResult = await checkCustomerUpdate(offerData.buyer);
        if (customerValidationResult === false) {
            // Dialog wurde gezeigt oder Validierung fehlgeschlagen, Prozess stoppen
            return;
        }

        // Produktvalidierung durchführen
        const productValidationResult = await checkAndHandleProductUpdate(offerData.items as any);
        if (productValidationResult === false) {
            // Dialog wurde gezeigt, Prozess wird vom Dialog-Handler fortgesetzt
            return;
        }

        // Wenn keine Dialoge angezeigt wurden, Übersetzung prüfen und senden
        const documentLanguage = offerData.documentLanguage || 'de';
        const preferredLang = sellerData.preferredLanguage || 'de';

        if (documentLanguage !== preferredLang) {
            const formDataForTranslation = {
                items: offerData.items.map(item => ({
                    ...item,
                    productId: getProductMetaForItems()[item.id]?.productId || null,
                })),
                notes: offerData.notes,
                offerText: offerData.offerText,
            };
            await prepareTranslationModal(
                formDataForTranslation,
                documentLanguage,
                true, // isOffer
                async (translatedData) => {
                    await submitOfferToBackend({
                        items: translatedData.items,
                        notes: translatedData.notes,
                        offerText: translatedData.offerText,
                    });
                }
            );
        } else {
            await submitOfferToBackend();
        }
    };

    // Draft speichern
    const handleSaveDraft = async () => {
        if (!token) {
            setError("Authentifizierungstoken fehlt. Bitte neu anmelden.");
            return;
        }
        if (!sellerData) {
            setError("Verkäuferdaten fehlen.");
            return;
        }

        setIsSavingDraft(true);
        setError(null);

        const dataToSend: OfferFormData = {
            ...offerData,
            seller: sellerData!,
            calculatedTotals: calculatedTotals,
        };
        
        try {
            const { draftId } = await saveDraft(dataToSend, token);
            logger.info(`Draft erfolgreich gespeichert mit ID: ${draftId}.`);
            
            // Draft-Liste neu laden
            await fetchDrafts();
            
            setError(null);
        } catch (err: any) {
            console.error('Fehler beim Speichern des Drafts:', err);
            setError(`Fehler: ${err.message || 'Unbekannter Fehler beim Speichern des Drafts'}`);
        } finally {
            setIsSavingDraft(false);
        }
    };

    // Vorschau generieren
    const handleGeneratePreview = async () => {
        if (!token) {
            setError("Authentifizierungstoken fehlt. Bitte neu anmelden.");
            return;
        }
        if (!sellerData) {
            setError("Verkäuferdaten fehlen.");
            return;
        }

        setIsGeneratingPreview(true);
        setError(null);

        const dataToSend: OfferFormData = {
            ...offerData,
            seller: sellerData!,
            calculatedTotals: calculatedTotals,
        };
        
        try {
            const { pdfBlob, draftId } = await generateOfferPreview(dataToSend, token);
            
            if (!pdfBlob) {
                throw new Error("Kein PDF-Blob vom Backend erhalten.");
            }

            // Sicherstellen, dass der Blob tatsächlich ein PDF ist
            const correctedPdfBlob = pdfBlob.type !== 'application/pdf' 
                ? new Blob([pdfBlob], { type: 'application/pdf' })
                : pdfBlob;
                
            // Erstelle eine URL für den Download
            const url = URL.createObjectURL(correctedPdfBlob);
            
            // Professioneller Dateiname für Vorschau: [Kundenname]-Angebot-Vorschau-[Datum].pdf
            const previewCustomerName = (offerData.buyer.companyName || offerData.buyer.personName || offerData.buyer.name || 'Kunde')
                .replace(/[/\\?%*:|"<>äöüÄÖÜß]/g, (char) => {
                    const umlautMap: Record<string, string> = { 'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'Ä': 'Ae', 'Ö': 'Oe', 'Ü': 'Ue', 'ß': 'ss' };
                    return umlautMap[char] || '_';
                })
                .replace(/\s+/g, '_')
                .substring(0, 50);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${previewCustomerName}-Angebot-Vorschau-${new Date().toISOString().split('T')[0]}.pdf`;
            link.setAttribute('type', 'application/pdf');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Blob-URL nach Verwendung freigeben
            setTimeout(() => URL.revokeObjectURL(url), 5000);
            
            logger.info(`Angebots-Vorschau erfolgreich erstellt mit Draft-ID: ${draftId}.`);
            
            // Draft-Liste neu laden
            await fetchDrafts();
            
            setError(null);
        } catch (err: any) {
            console.error('Fehler beim Generieren der Vorschau:', err);
            setError(`Fehler: ${err.message || 'Unbekannter Fehler beim Generieren der Vorschau'}`);
        } finally {
            setIsGeneratingPreview(false);
        }
    };

    // Draft auswählen
    const handleSelectDraft = async (selectedOption: SingleValue<OfferDraftOption>) => {
        const draftId = selectedOption ? selectedOption.value : null;
        
        if (draftId === null) {
            // Benutzer wählt "Neuer Entwurf" -> Formular zurücksetzen
            resetOfferForm();
            setSelectedDraftId(null);
            return;
        }

        if (!token) {
            setError("Authentifizierungstoken fehlt. Bitte neu anmelden.");
            return;
        }

        setSelectedDraftId(draftId);
        setIsLoadingDraftDetails(true);
        setError(null);
        setPdfUrl(null);

        try {
            const details = await getDraftDetails(draftId, token);
            logger.info("Geladene Draft-Details:", details);
            
            // Setze Formulardaten basierend auf den geladenen Draft-Details
            setOfferData((prevOfferData) => ({
                ...prevOfferData,
                ...details.rawData,
                
                // Verkäuferdaten aus dem aktuellen Verkäuferprofil übernehmen
                seller: sellerData || defaultOfferFormData.seller,
                offerNumberFormat: sellerData?.offerNumberFormat, 
                offerNumberCurrent: sellerData?.offerNumberCurrent,
                
                // ⭐ NEU: Dokumentsprache wiederherstellen (falls gespeichert)
                documentLanguage: details.rawData?.documentLanguage || 'de',
                
                // Draft-spezifische Felder
                basedOnDraftId: draftId,
            }));
        } catch (err: any) {
            logger.error(err, `Fehler beim Laden der Details für Draft ${draftId}`);
            setError(err.message || 'Fehler beim Laden der Draft-Details.');
            setSelectedDraftId(null);
        } finally {
            setIsLoadingDraftDetails(false);
        }
    };

    // Draft löschen Handler mit optimistic update
    const handleDeleteDraft = async (draftId: string) => {
        if (!token) {
            console.error('Kein Auth-Token verfügbar für Draft-Löschung');
            return;
        }

        // Optimistic Update: Sofort aus der Liste entfernen
        const originalDrafts = [...availableDrafts];
        const filteredDrafts = availableDrafts.filter(draft => draft.id !== draftId);
        setAvailableDrafts(filteredDrafts);

        // Wenn der gelöschte Draft der aktuell ausgewählte war, Auswahl zurücksetzen
        if (selectedDraftId === draftId) {
            setSelectedDraftId(null);
        }

        try {
            // Backend-Löschung im Hintergrund
            await deleteDraft(draftId, token);
            console.log('Draft erfolgreich gelöscht:', draftId);
        } catch (error: any) {
            console.error('Fehler beim Löschen des Drafts:', error);
            
            // Bei Fehler: Original-Liste wiederherstellen
            setAvailableDrafts(originalDrafts);
            if (selectedDraftId === draftId) {
                setSelectedDraftId(draftId); // Auswahl wiederherstellen
            }
            
            setDraftListError('Fehler beim Löschen des Entwurfs: ' + error.message);
        }
    };

    // --- UI Rendering --- 

    // Wiederherstellen: Optionen für react-select
    const offerSelectOptions: OfferSelectOption[] = availableOffers.map((offer: OfferListItem) => ({
        value: offer.id,
        label: `Angebot ${offer.offerNumber} (${offer.buyerName}, ${formatDateCentral(offer.offerDate, { locale: i18n.language })})`
    }));

    // Wiederherstellen: Aktuell ausgewählte Option für react-select
    const selectedOfferOption = offerSelectOptions.find(option => option.value === selectedOfferId);

    if (loading || sellerDataLoading) {
        return <LoadingSpinner fullScreen label="Lade Benutzerdaten..." />;
    }

    if (!currentUser) {
        return <ErrorDisplay message="Bitte melden Sie sich an, um Angebote zu erstellen." />;
    }

    return (
        <div className="space-y-6">
                            <h1 className="text-3xl font-bold text-primary">{t('title')}</h1>

            {/* Draft-Auswahl */}
            <OfferDraftSelector 
                drafts={availableDrafts}
                selectedDraftId={selectedDraftId}
                onSelectDraft={handleSelectDraft}
                onDeleteDraft={handleDeleteDraft}
                isLoading={isLoadingDrafts}
                error={draftListError}
            />
            {isLoadingDraftDetails && <LoadingSpinner label="Lade Entwurf-Details..." />}

            {/* Bereich für Angebotsauswahl */ }
            <div className="bg-surface-raised shadow-lg rounded-lg p-4 border border-hairline">
                 <h2 className="text-lg font-semibold text-primary mb-3">{t('existingOffer.title')}</h2>
                 <Select<OfferSelectOption> 
                     options={offerSelectOptions}
                     value={selectedOfferOption || null}
                     onChange={handleSelectOffer}
                                                 placeholder={t('existingOffer.selectPlaceholder')}
                     isLoading={isLoadingOffers}
                     isClearable={true}
                     noOptionsMessage={() => isLoadingOffers ? 'Lade Angebote...' : 'Keine Angebote gefunden'}
                     styles={reactSelectTokenStyles}
                 />
                 {offerListError && <p className="text-[var(--tone-danger-fg)] text-sm mt-1">{offerListError}</p>}
                 {isLoadingOfferDetails && <LoadingSpinner label="Lade Angebotsdetails..." className="mt-2"/>}
            </div>

            {error && (
                 <ErrorDisplay message={error} />
            )}

            {/* Layout: Nur eine Spalte */}
            <div className="grid grid-cols-1 gap-6">
                {/* Hauptformular-Container */}
                <div className="bg-surface-raised shadow-lg rounded-lg p-6 border border-hairline space-y-6">
                     {/* Käufer */}
                     <div className="p-6 bg-surface-raised rounded-lg border border-hairline shadow-inner">
                         <h2 className="text-xl font-semibold mb-4 border-b border-strong pb-2 text-primary">{t('sections.buyerInformation')}</h2>
                         <BuyerDetailsForm 
                             buyer={offerData.buyer} 
                             setBuyer={handleBuyerChange}
                             onCustomerSelect={handleCustomerSelection}
                             showTitle={false}
                         />
                     </div>
                     
                     {/* Angebotsdetails */}
                     <div className="p-6 bg-surface-raised rounded-lg border border-hairline shadow-inner">
                         <h2 className="text-xl font-semibold mb-4 border-b border-strong pb-2 text-primary">{t('sections.offerDetails')}</h2>
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                             <div>
                                 <label htmlFor="offerDate" className="block text-sm font-medium text-secondary">{t('details.offerDate')}</label>
                                 <input
                                     type="date"
                                     id="offerDate"
                                     name="offerDate"
                                     value={offerData.offerDate}
                                     onChange={handleDateChange}
                                     required
                                     className="mt-1 block w-full px-3 py-2 bg-surface-inset border border-strong rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-primary"
                                 />
                             </div>
                             <div>
                                 <label htmlFor="validUntil" className="block text-sm font-medium text-secondary">{t('details.validUntil')}</label>
                                 <input
                                     type="date"
                                     id="validUntil"
                                     name="validUntil"
                                     value={offerData.validUntil || ''}
                                     onChange={handleValidUntilChange}
                                     className="mt-1 block w-full px-3 py-2 bg-surface-inset border border-strong rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-primary"
                                 />
                             </div>
                         </div>
                        <div className="mt-4">
                                                            <label htmlFor="currentOfferNumber" className="block text-sm font-medium text-secondary">{t('details.nextOfferNumber')}</label>
                            <input
                                type="text"
                                id="currentOfferNumber"
                                value={offerNumberPreview}
                                readOnly
                                className="mt-1 block w-full px-3 py-2 bg-surface-hover border border-gray-500 rounded-md shadow-sm sm:text-sm text-secondary cursor-not-allowed"
                            />
                        </div>
                        
                        {/* ⭐ NEU: Dokumentsprache-Auswahl */}
                        <div className="mt-6 pt-6 border-t border-hairline">
                            <DocumentLanguageSelector
                                value={offerData.documentLanguage || 'de'}
                                onChange={(language) => setOfferData(prev => ({ ...prev, documentLanguage: language }))}
                                disabled={isGenerating}
                            />
                        </div>
                    </div>
                     
                     {/* Positionen - Using InvoiceItemsForm */}
                     <div className="p-6 bg-surface-raised rounded-lg border border-hairline shadow-inner">
                         <h2 className="text-xl font-semibold mb-4 border-b border-strong pb-2 text-primary">{t('sections.offerPositions')}</h2>
                         <InvoiceItemsForm
                             items={offerData.items as unknown as InvoiceItem[]}
                             setItems={handleItemsFormChange}
                             currency={offerData.currency}
                             showTitle={false}
                             onProductSelect={handleProductSelectionForHook}
                         />
                         <div className="mt-4 text-right space-y-1 text-sm text-primary">
                             <p>{t('totals.subtotalNet')}: {calculatedTotals?.totalNet.toFixed(2)} {offerData.currency}</p>
                             {calculatedTotals && Object.entries(calculatedTotals.taxSummary).map(([rate, values]) => {
                                 const typedValues = values as { base: number; amount: number };
                                 return (
                                                                            <p key={rate}>+ {rate}% {t('totals.vatLabel')}: {typedValues.amount.toFixed(2)} {offerData.currency}</p>
                                 );
                             })}
                             <p className="font-bold text-base">{t('totals.totalAmount')}: {calculatedTotals?.grandTotal.toFixed(2)} {offerData.currency}</p>
                         </div>
                     </div>
                     
                     {/* Texte & Notizen */}
                     <div className="p-6 bg-surface-raised rounded-lg border border-hairline shadow-inner">
                         <h2 className="text-xl font-semibold mb-4 border-b border-strong pb-2 text-primary">{t('sections.textsAndNotes')}</h2>
                         <div>
                             <label htmlFor="offerText" className="block text-sm font-medium text-secondary">{t('texts.introText')}</label>
                             <textarea
                                 id="offerText"
                                 name="offerText"
                                 rows={3}
                                 value={offerData.offerText || ''}
                                 onChange={handleOfferTextChange}
                                 className="mt-1 block w-full px-3 py-2 bg-surface-inset border border-strong rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-primary"
                                 placeholder={t('texts.introTextPlaceholder')}
                             />
                         </div>
                         <div className="mt-4">
                             <label htmlFor="notes" className="block text-sm font-medium text-secondary">{t('texts.finalText')}</label>
                             <textarea
                                 id="notes"
                                 name="notes"
                                 rows={3}
                                 value={offerData.notes || ''}
                                 onChange={handleNotesChange}
                                 className="mt-1 block w-full px-3 py-2 bg-surface-inset border border-strong rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-primary"
                                 placeholder={t('texts.finalTextPlaceholder')}
                             />
                         </div>
                     </div>

                     {/* Per Email versenden */}
                     <div className="mt-6 border-t border-hairline pt-4">
                         <h3 className="text-lg font-medium text-accent-ink mb-3">
                             {t('emailSend.sectionTitle', { ns: 'common' })}
                         </h3>
                         <div className="flex items-center gap-2 mb-3">
                             <input
                                 type="checkbox"
                                 id="sendToEmailToggle"
                                 checked={!!sendToEmail}
                                 onChange={(e) => {
                                     if (e.target.checked) {
                                         setSendToEmail(offerData.buyer?.email || '');
                                     } else {
                                         setSendToEmail(null);
                                     }
                                 }}
                                 className="w-4 h-4 accent-indigo-500"
                             />
                             <label htmlFor="sendToEmailToggle" className="text-sm font-medium text-secondary">
                                 {t('emailSend.offerToggleLabel', { ns: 'common' })}
                             </label>
                         </div>
                         {sendToEmail !== null && (
                             <div>
                                 <label htmlFor="sendToEmailInput" className="block text-sm font-medium text-secondary mb-1">
                                     {t('emailSend.emailLabel', { ns: 'common' })}
                                 </label>
                                 <input
                                     type="email"
                                     id="sendToEmailInput"
                                     value={sendToEmail}
                                     onChange={(e) => setSendToEmail(e.target.value)}
                                     className="mt-1 block w-full px-3 py-2 bg-surface-inset border border-strong rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-primary"
                                     placeholder={t('emailSend.emailPlaceholder', { ns: 'common' })}
                                 />
                                 <p className="text-xs text-muted mt-1">
                                     {t('emailSend.hint', { ns: 'common' })}
                                 </p>
                             </div>
                         )}
                     </div>

                     {/* Actions */}
                     <OfferFormActions
                         onGenerate={handleGenerateOffer}
                         isGenerating={isGenerating}
                         onSaveDraft={handleSaveDraft}
                         onGeneratePreview={handleGeneratePreview}
                         isSavingDraft={isSavingDraft}
                         isGeneratingPreview={isGeneratingPreview}
                     />
                </div>
            </div>
            
            {/* NEU: Kundendialog hinzufügen (genau wie in InvoiceDashboard) */}
            <ConfirmationDialog 
                isOpen={customerDialogOpen}
                title={customerDialogConfig.title}
                message={customerDialogConfig.message}
                options={customerDialogConfig.options || []}
                onClose={() => setCustomerDialogOpen(false)}
                dataComparison={customerDialogConfig.action === 'update' ? {
                    original: customerDialogConfig.original || {},
                    modified: customerDialogConfig.data,
                    displayFields: [
                        { key: 'name', label: 'Name' },
                        { key: 'companyName', label: 'Firmenname' },
                        { key: 'personName', label: 'Ansprechpartner' },
                        { key: 'street', label: 'Straße' },
                        { key: 'zip', label: 'PLZ' },
                        { key: 'city', label: 'Ort' },
                        { key: 'country', label: 'Land' }
                    ]
                } : undefined}
            />

            {/* NEU: Produktdialog hinzufügen (genau wie in InvoiceDashboard) */}
            <ConfirmationDialog 
                isOpen={productDialogOpen}
                title={productDialogConfig.title}
                message={productDialogConfig.message}
                options={[
                    { 
                        label: 'Änderungen speichern', 
                        action: async () => {
                            const result = await handleProductDialogAction('process');
                            if (result === 'continue') {
                                // Nach erfolgreicher Produktverarbeitung Übersetzung prüfen und Angebot generieren
                                const docLang = offerData.documentLanguage || 'de';
                                const prefLang = sellerData?.preferredLanguage || 'de';
                                if (docLang !== prefLang) {
                                    const formDataForTranslation = {
                                        items: offerData.items.map(item => ({
                                            ...item,
                                            productId: getProductMetaForItems()[item.id]?.productId || null,
                                        })),
                                        notes: offerData.notes,
                                        offerText: offerData.offerText,
                                    };
                                    await prepareTranslationModal(
                                        formDataForTranslation, docLang, true,
                                        async (td) => submitOfferToBackend({ items: td.items, notes: td.notes, offerText: td.offerText })
                                    );
                                } else {
                                    await submitOfferToBackend();
                                }
                            }
                        },
                        variant: 'primary' 
                    },
                    { 
                        label: 'Abbrechen', 
                        action: async () => {
                            await handleProductDialogAction('cancel');
                        },
                        variant: 'secondary'
                    }
                ]}
                onClose={() => setProductDialogOpen(false)}
                dataComparison={productDialogConfig.itemsToProcess.length === 1 && productDialogConfig.itemsToProcess[0].action === 'update' ? {
                    original: productDialogConfig.itemsToProcess[0].originalProduct || {},
                    modified: productDialogConfig.itemsToProcess[0].item,
                    displayFields: [
                        { key: 'name', label: 'Name' },
                        { key: 'description', label: 'Beschreibung' },
                        { key: 'netPrice', label: 'Netto-Preis' },
                        { key: 'taxRate', label: 'Steuersatz' },
                        { key: 'unitCode', label: 'Einheit' }
                    ]
                } : undefined}
            />

            <TranslationReviewModal
                isOpen={isTranslationModalOpen}
                isLoading={isTranslationLoading}
                originalLanguage={sellerData?.preferredLanguage || 'de'}
                targetLanguage={offerData.documentLanguage || 'de'}
                fields={translationFields}
                onFieldChange={handleTranslationFieldChange}
                onConfirm={() => confirmTranslation(translationFields, offerData.documentLanguage || 'de')}
                onCancel={cancelTranslation}
            />
        </div>
    );
};

export default OfferDashboard;